/* [Signature Legacy] Trust & Promise — Luxury Slider (v2 · 2026-10-22)
   - 높이 계산 없음: 레이아웃은 CSS Grid 스택이 담당
   - 자동재생 + 청자색 진행 표시(남은 시간 정확히 이어서 재생)
   - 마우스 오버 / 포커스 / 화면 밖 / 탭 비활성 시 일시정지
   - 키보드 ←/→/Home/End, 테마 편집기(section load / block select) 대응
*/
 
(() => {
  const ROOT_SEL = '[data-sigtrust]';
  const instances = new WeakMap();
 
  const prefersReducedMotion = () =>
    !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
 
  class SigTrustSlider {
    constructor(root) {
      this.root = root;
      this.promise = root.querySelector('.sigtrust__promise');
      this.slides = Array.from(root.querySelectorAll('.sigtrust__slide'));
      this.dots = Array.from(root.querySelectorAll('.sigtrust__dot'));
      this.interval = Math.max(2000, Number(root.dataset.interval) || 6000);
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.timer = null;
      this.deadline = 0;
      this.remaining = this.interval;
      this.pauseReasons = new Set();
      this.cleanups = [];
 
      if (this.slides.length === 0) return;
 
      this.autoplay = this.slides.length > 1 && !prefersReducedMotion();
      root.dataset.autoplay = this.autoplay ? 'on' : 'off';
 
      this.bindDots();
      this.bindPause();
      this.go(this.index, { focus: false, restart: true });
    }
 
    on(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this.cleanups.push(() => target.removeEventListener(type, fn, opts));
    }
 
    /* ---------- navigation ---------- */
    go(next, { focus = false, restart = true } = {}) {
      const total = this.slides.length;
      this.index = ((next % total) + total) % total;
 
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
        if ('inert' in slide) slide.inert = !active;
      });
 
      this.dots.forEach((dot, i) => {
        const active = i === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
        dot.tabIndex = active ? 0 : -1;
      });
 
      if (focus && this.dots[this.index]) this.dots[this.index].focus();
      if (restart) this.restartTimer();
    }
 
    bindDots() {
      this.dots.forEach((dot, i) => {
        this.on(dot, 'click', () => this.go(i));
      });
 
      const list = this.root.querySelector('.sigtrust__dots');
      if (!list) return;
 
      this.on(list, 'keydown', (e) => {
        const map = {
          ArrowRight: this.index + 1,
          ArrowLeft: this.index - 1,
          Home: 0,
          End: this.slides.length - 1,
        };
        if (!(e.key in map)) return;
        e.preventDefault();
        this.go(map[e.key], { focus: true });
      });
    }
 
    /* ---------- autoplay ---------- */
    clearTimer() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = null;
    }
 
    schedule(ms) {
      this.clearTimer();
      if (!this.autoplay || this.pauseReasons.size > 0) return;
      this.deadline = Date.now() + ms;
      this.timer = setTimeout(() => this.go(this.index + 1), ms);
    }
 
    restartTimer() {
      this.remaining = this.interval;
      // 진행 표시 애니메이션을 처음부터 다시 시작 (같은 원형을 다시 눌렀을 때 포함)
      const dot = this.dots[this.index];
      if (dot) {
        dot.classList.remove('is-active');
        void dot.offsetWidth; // reflow
        dot.classList.add('is-active');
      }
      this.schedule(this.remaining);
    }
 
    pause(reason) {
      if (!this.autoplay) return;
      if (this.pauseReasons.size === 0 && this.timer) {
        this.remaining = Math.max(0, this.deadline - Date.now());
      }
      this.pauseReasons.add(reason);
      this.clearTimer();
      this.root.classList.add('is-paused');
    }
 
    resume(reason) {
      if (!this.autoplay || !this.pauseReasons.has(reason)) return;
      this.pauseReasons.delete(reason);
      if (this.pauseReasons.size > 0) return;
      this.root.classList.remove('is-paused');
      this.schedule(this.remaining > 50 ? this.remaining : this.interval);
    }
 
    bindPause() {
      if (!this.autoplay) return;
      const area = this.promise || this.root;
 
      this.on(area, 'mouseenter', () => this.pause('hover'));
      this.on(area, 'mouseleave', () => this.resume('hover'));
      this.on(area, 'focusin', () => this.pause('focus'));
      this.on(area, 'focusout', (e) => {
        if (!area.contains(e.relatedTarget)) this.resume('focus');
      });
 
      this.on(document, 'visibilitychange', () => {
        if (document.hidden) this.pause('tab');
        else this.resume('tab');
      });
 
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.resume('view');
              else this.pause('view');
            });
          },
          { threshold: 0.25 }
        );
        this.io.observe(area);
      }
    }
 
    destroy() {
      this.clearTimer();
      if (this.io) this.io.disconnect();
      this.cleanups.forEach((fn) => fn());
      this.cleanups = [];
    }
  }
 
  /* ---------- boot ---------- */
  const mount = (scope = document) => {
    scope.querySelectorAll(ROOT_SEL).forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new SigTrustSlider(root));
    });
  };
 
  const unmount = (scope) => {
    scope.querySelectorAll(ROOT_SEL).forEach((root) => {
      const inst = instances.get(root);
      if (inst) inst.destroy();
      instances.delete(root);
    });
  };
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount());
  } else {
    mount();
  }
 
  /* Shopify Theme Editor */
  document.addEventListener('shopify:section:load', (e) => mount(e.target));
  document.addEventListener('shopify:section:unload', (e) => unmount(e.target));
 
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest && e.target.closest('.sigtrust__slide');
    if (!slide) return;
    const root = slide.closest(ROOT_SEL);
    const inst = root && instances.get(root);
    if (!inst) return;
    inst.go(Number(slide.dataset.slide) || 0, { restart: false });
    inst.pause('editor');
  });
 
  document.addEventListener('shopify:block:deselect', (e) => {
    const root = e.target.closest && e.target.closest(ROOT_SEL);
    const inst = root && instances.get(root);
    if (inst) inst.resume('editor');
  });
})();
 